import { Link, useNavigate } from "react-router-dom";
import { Hourglass, Mail, LogOut, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/** Awaiting Approval — full-screen lock for users whose account
 *  status != "active". Renders instead of dashboard / project board /
 *  exports for any non-admin user without an approved status.
 *
 *  No app shell, no sidebar — completely takes over the viewport so the
 *  user can't reach any gated route. The route guard in `ProtectedRoute`
 *  redirects every other path here while the account is pending.
 */
export default function Pending() {
    const { user, logout } = useAuth();
    const nav = useNavigate();

    const mailAdmin = () => {
        const subject = encodeURIComponent("Furmais — Approval request");
        const body = encodeURIComponent(
            `Hi admin, my account (${user?.email || "—"}) is awaiting approval. Please activate it when convenient.`
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    return (
        <div
            className="min-h-screen relative overflow-hidden bg-[#050505] text-white flex items-center justify-center p-6"
            data-testid="pending-page"
        >
            {/* Decorative glow ring */}
            <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(800px 400px at 50% 30%, rgba(255,68,0,0.08), transparent 70%)",
                }}
            />

            <div className="relative z-10 w-full max-w-xl text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm border border-[#FF4400]/40 bg-[#FF4400]/10 mb-6 ceo-glow">
                    <Hourglass className="w-6 h-6 text-[#FF4400]" />
                </div>

                <div className="label-micro mb-3 text-[#FF4400]">Account Status · Pending</div>
                <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">
                    Awaiting Approval
                </h1>
                <p className="text-zinc-400 mt-4 leading-relaxed text-base">
                    Thanks for signing up. Your Furmais account is under
                    review by our team. We typically activate new workspaces
                    within one business day. You'll receive an email at
                    {" "}<span className="text-zinc-200">{user?.email || "your inbox"}</span>{" "}
                    as soon as your access is approved.
                </p>
                <p className="text-zinc-500 mt-3 text-sm">
                    Have a question or need faster activation? Use{" "}
                    <span className="text-zinc-300">Contact admin</span>{" "}
                    below — we read every message.
                </p>

                <div className="mt-6 inline-block px-3 py-1.5 rounded-sm border border-zinc-900 bg-zinc-950 text-[11px] font-mono uppercase tracking-wider text-zinc-500">
                    Signed in as <span className="text-zinc-200">{user?.email || "—"}</span>
                </div>

                <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                    <button
                        type="button"
                        data-testid="pending-contact"
                        onClick={mailAdmin}
                        className="btn-accent text-sm"
                    >
                        <Mail className="w-4 h-4" /> Contact admin
                    </button>
                    <Link
                        to="/profile"
                        data-testid="pending-profile"
                        className="btn-secondary text-sm"
                    >
                        <User className="w-4 h-4" /> View profile
                    </Link>
                    <button
                        type="button"
                        data-testid="pending-logout"
                        onClick={() => { logout(); nav("/login"); }}
                        className="text-sm text-zinc-400 hover:text-white flex items-center gap-1.5 px-3 py-2"
                    >
                        <LogOut className="w-4 h-4" /> Log out
                    </button>
                </div>

                <div className="mt-12 text-[10px] font-mono uppercase tracking-wider text-zinc-700">
                    Furmais · Controlled SaaS · Admin-approved onboarding
                </div>
            </div>
        </div>
    );
}
